import type { ErrorComponentProps } from "@tanstack/react-router";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { Home, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { ApplicationVersionErrorFallback } from "@/components/application-version-error-fallback";
import { Button } from "@/components/ui/button";
import { StatusPanel } from "@/components/ui/status-panel";
import { isChunkLoadError } from "@/lib/connection-errors";
import { captureError } from "@/lib/observability";

function getRouteErrorKey(error: unknown) {
  if (error instanceof Error) {
    return `${error.name}:${error.message}`;
  }
  return String(error);
}

/**
 * Error component for TanStack Router's `defaultErrorComponent`.
 *
 * Receives `{ error, reset }` from the router:
 * - `error`: The caught error object
 * - `reset`: Re-renders the route (clears error state)
 *
 * This catches errors thrown during route rendering or in `loader`/`beforeLoad`.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const routeContext = useRouterState({
    select: (state) => ({
      href: state.location.href,
      pathname: state.location.pathname,
      routeIds: state.matches.map((match) => match.id),
    }),
  });
  const errorKey = useMemo(() => getRouteErrorKey(error), [error]);
  const chunkLoadError = isChunkLoadError(error);
  const capturedErrorKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (chunkLoadError || capturedErrorKeyRef.current === errorKey) {
      return;
    }
    capturedErrorKeyRef.current = errorKey;

    captureError(error, {
      feature: "routing",
      operation: "route-error",
      extra: {
        href: routeContext.href,
        pathname: routeContext.pathname,
        routeIds: routeContext.routeIds,
      },
    });
  }, [
    chunkLoadError,
    error,
    errorKey,
    routeContext.href,
    routeContext.pathname,
    routeContext.routeIds,
  ]);

  if (chunkLoadError) {
    return <ApplicationVersionErrorFallback />;
  }

  return (
    <StatusPanel
      title="Something went wrong"
      description="An unexpected error occurred while loading this page."
      tone="destructive"
      size="page"
      className="flex-1"
      actions={
        <>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              reset();
              router.invalidate();
            }}
          >
            <RefreshCw data-icon="inline-start" />
            Try Again
          </Button>
          <Link to="/">
            <Button size="lg">
              <Home data-icon="inline-start" />
              Home
            </Button>
          </Link>
        </>
      }
    />
  );
}
